import { useCallback, useEffect, useRef, useState } from "react"
import { Stack } from "@mui/material"
import { RateCardEditorAlerts } from "./rate-card-editor-alerts"
import { useSave } from "lib/@hooks/use-save"
import { busyWhile } from "lib/@components/busy/busy-while"
import { confirm } from "lib/@dialogs/confirm"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { navigate } from "lib/routes/navigate"
import { SaveIndicator } from "lib/@components/save-indicator/save-indicator"
import { updateRateCard } from "routes/rate-cards/controller/update-rate-card"
import { cloneRateCard } from "routes/rate-cards/controller/clone-rate-card"
import { archiveRateCard } from "routes/rate-cards/controller/archive-rate-card"
import { dismissRateCardReview } from "routes/rate-cards/controller/dismiss-rate-card-review"
import { useDialog } from "lib/@hooks/useDialog"
import { exportRateCardCsv } from "./export-rate-card-csv"
import { ImportCsvDialog } from "./import-csv-dialog"
import { RateCardEditorMenu } from "./rate-card-editor-menu"
import { RateCardEditDetailsDialog } from "./rate-card-edit-details-dialog"
import { RateCardTradeSection } from "./rate-card-trade-section"
import { RateCardModifiersSection } from "./rate-card-modifiers-section"
import { sanitizeLines } from "./sanitize-rate-card-lines"
import { parseOptionalWorkingTime } from "./working-time-validation"

export function RateCardEditor({ card, id, menuAnchor, setMenuAnchor, readOnly }) {
    const [target, setTarget] = useState(toEditable)
    const [reviewDismissed, setReviewDismissed] = useState(false)
    const saving = useRef(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(resetTarget, [card])

    const save = useCallback(saveCard, [id, readOnly])
    const onChange = useSave({ save, target, id: "rateCard", wait: 1000 }, [id])
    const appliedDefault = useRef(false)
    useEffect(applyDefaultRateOnLoad, [card, onChange, readOnly])

    const openImportDialog = useDialog(<ImportCsvDialog />, { maxWidth: "sm", fullWidth: true })
    const openEditDetailsDialog = useDialog(<RateCardEditDetailsDialog />, { maxWidth: "sm", fullWidth: true })
    const menuActions = getMenuActions()

    return (
        <Stack data-cy="rate-card-view" spacing={0} sx={{ px: 0, pt: 2, pb: 4, flex: 1 }}>
            <SaveIndicator id="rateCard" />
            <RateCardEditorMenu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={closeMenu}
                card={card}
                {...menuActions}
            />

            <RateCardEditorAlerts
                card={card}
                reviewDismissed={reviewDismissed}
                onRestoreSkillMap={handleRestoreSkillMap}
                onDismissReview={menuActions.onDismissReview}
            />

            <RateCardTradeSection
                lines={target.lines}
                currency={target.currency}
                skillMapId={card.skillMapId}
                onUpdateLine={handleUpdateLine}
                readOnly={readOnly}
            />
            <RateCardModifiersSection
                modifiers={target.modifiers}
                tradeLines={target.lines}
                currency={target.currency}
                onUpdate={handleUpdateModifiers}
                readOnly={readOnly}
            />
        </Stack>
    )

    function toEditable() {
        const rate = card.defaultRate
        const lines = (card.lines || []).map(cloneLine)
        const allZero = rate != null && rate > 0 && lines.every(lineHasNoRate)
        return {
            name: card.name || "",
            description: card.description || "",
            lines: allZero ? lines.map((l) => ({ ...l, hourlyRate: rate })) : lines,
            modifiers: (card.modifiers || []).map((m) => ({ ...m, scope: { ...m.scope } })),
            currency: card.currency || "GBP",
            defaultRate: rate,
            hoursPerDay: card.hoursPerDay ?? null,
        }
    }

    function resetTarget() {
        if (saving.current) {
            saving.current = false
            return
        }
        appliedDefault.current = false
        setTarget(toEditable())
        setReviewDismissed(false)
    }

    function applyDefaultRateOnLoad() {
        if (readOnly) return
        if (appliedDefault.current) return
        const rate = card.defaultRate
        const lines = card.lines || []
        if (rate != null && rate > 0 && lines.every((l) => !l.hourlyRate)) {
            appliedDefault.current = true
            onChange()
        }
    }

    function handleUpdateLine(tradeId, field, value) {
        if (readOnly) return
        setTarget((prev) => {
            const updated = {
                ...prev,
                lines: prev.lines.map((l) => {
                    if (l.tradeId === tradeId) return { ...l, [field]: value }
                    return l
                }),
            }
            return updated
        })
        onChange()
    }

    function handleUpdateModifiers(nextModifiers) {
        if (readOnly) return
        setTarget((prev) => ({ ...prev, modifiers: nextModifiers }))
        onChange()
    }

    async function saveCard(current) {
        if (readOnly) return
        saving.current = true
        try {
            await updateRateCard({
                id,
                data: {
                    name: current.name,
                    description: current.description,
                    lines: sanitizeLines(current.lines),
                    modifiers: sanitizeModifiers(current.modifiers),
                    currency: current.currency,
                    defaultRate: current.defaultRate,
                    hoursPerDay: current.hoursPerDay ?? null,
                },
            })
        } catch (e) {
            saving.current = false
            throw e
        }
    }

    function closeMenu() {
        setMenuAnchor(null)
    }

    function getMenuActions() {
        const actions = {
            onViewSkillMap: handleViewSkillMap,
            onExportCsv: handleExportCsv,
        }
        if (readOnly) return actions

        return {
            ...actions,
            onRename: handleEditDetails,
            onImportCsv: handleImportCsv,
            onDuplicate: handleDuplicate,
            onArchive: handleArchive,
            onDismissReview: handleDismissReview,
        }
    }

    async function handleEditDetails() {
        closeMenu()
        const result = await openEditDetailsDialog({
            currentId: id,
            name: target.name,
            description: target.description,
            currency: target.currency,
            defaultRate: target.defaultRate,
            hoursPerDay: target.hoursPerDay,
        })
        if (!result) return
        const parsedRate = typeof result.defaultRate === "string" ? parseFloat(result.defaultRate) : result.defaultRate
        const newRate = parsedRate == null || Number.isNaN(parsedRate) ? null : parsedRate
        const oldRate = target.defaultRate
        const rateChanged = newRate != null && newRate !== oldRate

        let applyToAll = false
        if (rateChanged) {
            const hasCustomRates = hasCustomTradeRates(oldRate)
            if (hasCustomRates) {
                applyToAll = await confirm(
                    "Some trades already have custom hourly rates. Do you want to overwrite all trade rates with the new default rate? Choose 'Cancel' to keep existing rates.",
                    "Apply Default Rate to All Trades"
                )
            } else {
                applyToAll = true
            }
        }

        setTarget((prev) => ({
            ...prev,
            name: result.name.trim(),
            description: result.description || "",
            currency: result.currency || "GBP",
            defaultRate: newRate,
            hoursPerDay: parseOptionalWorkingTime(result.hoursPerDay),
            lines: applyToAll ? prev.lines.map((l) => ({ ...l, hourlyRate: newRate })) : prev.lines,
        }))
        onChange()
    }

    function handleViewSkillMap() {
        if (!card.skillMapId) return
        closeMenu()
        navigate(`/app/optimiser/skill-mappings/${card.skillMapId}`)
    }

    function handleExportCsv() {
        closeMenu()
        exportRateCardCsv({
            card: {
                ...card,
                lines: target.lines,
                modifiers: target.modifiers,
                name: target.name,
                description: target.description,
                currency: target.currency,
                defaultRate: target.defaultRate,
            },
        })
    }

    async function handleImportCsv() {
        closeMenu()
        const importedLines = await openImportDialog({ card: { ...card, lines: target.lines } })
        if (!importedLines) return
        const importedById = {}
        for (const line of importedLines) {
            importedById[line.tradeId] = line
        }
        setTarget((prev) => ({
            ...prev,
            lines: prev.lines.map((existing) => {
                const imported = importedById[existing.tradeId]
                if (!imported) return existing
                const next = {
                    ...existing,
                    hourlyRate: imported.hourlyRate,
                    minimumBillingPeriod: imported.minimumBillingPeriod,
                    notes: imported.notes,
                    // mode / fixedAnnualAmount / contractedDaysPerYear are only present when the
                    // CSV had the matching column; otherwise preserve the existing line's values.
                    ...(imported.mode !== undefined && { mode: imported.mode }),
                    ...(imported.fixedAnnualAmount !== undefined && {
                        fixedAnnualAmount: imported.fixedAnnualAmount,
                    }),
                    ...(imported.contractedDaysPerYear !== undefined && {
                        contractedDaysPerYear: imported.contractedDaysPerYear,
                    }),
                }
                return next
            }),
        }))
        onChange()
        successSnackbar("CSV imported successfully")
    }

    async function handleDismissReview() {
        await busyWhile(() => dismissRateCardReview({ id }), "Dismissing review...")
        setReviewDismissed(true)
        successSnackbar("Review dismissed")
    }

    function handleRestoreSkillMap() {
        if (!card.skillMapId) return
        navigate(`/app/optimiser/skill-mappings/${card.skillMapId}`)
    }

    async function handleDuplicate() {
        closeMenu()
        await saveCard(target)
        const newCard = await busyWhile(cloneCurrentCard, "Duplicating rate card...")
        if (newCard?.id) {
            saving.current = false
            navigate(`/app/optimiser/rate-cards/${newCard.id}`)
            successSnackbar("Rate card duplicated")
        }
    }

    async function handleArchive() {
        closeMenu()
        await saveCard(target)
        const ok = await confirm(
            `Archive "${card.name}"? It can be restored later from the Archived filter.`,
            "Archive Rate Card"
        )
        if (!ok) return
        await busyWhile(() => archiveRateCard({ id }), "Archiving rate card...")
        navigate("/app/optimiser?optimiserTab=rate-cards")
        successSnackbar(`Rate card "${card.name}" archived`)
    }

    function cloneLine(l) {
        return { ...l }
    }

    function lineHasNoRate(l) {
        return !l.hourlyRate
    }

    function hasCustomTradeRates(baseRate) {
        return target.lines.some((l) => l.hourlyRate && l.hourlyRate !== (baseRate ?? 0))
    }

    function cloneCurrentCard() {
        return cloneRateCard({ id })
    }
}

function sanitizeModifiers(modifiers) {
    return (modifiers || []).map((m) => ({
        id: m.id,
        name: m.name,
        type: m.type,
        scope: { kind: m.scope?.kind || "whole-regime", value: m.scope?.value ?? null },
        value: m.value,
        applyTo: m.applyTo || "running",
    }))
}
